import Controller from "@/controllers/index";
import { SET } from "@/stores/CommumActionTypes";
import store from "@/stores/store";

/**
 * Cria o state de FrequencyTrendSignal.
 * @returns State de FrequencyTrendSignal.
 */
const EmptyFrequencyTrendSignalStateFactory = function()
{
	return {
		assetsFrequencyTrendSettings:   EmptyTrendSignalSettingFactory(),
		selectedFrequencyTrendSettings: [],
		menuOpened:                     false,
		availableCommonSignals:         [],
		availableFrequencyTrendSignals: [],
		CommonGroupExpanded:            true,
		availableDistinctSignalGroups:  [],
		DistinctGroupExpanded:          true,
		expandedDistinctAssetGroups:    [],
		ShowSignalNameDialog:           false,
		noDataAvailable:                false,
		filterText:                     "",

	};
};

/**
 * Cria o state de TrendSignalSetting.
 * @returns State de TrendSignalSetting.
 */
const EmptyTrendSignalSettingsStateFactory = function()
{
	return {
		assetsTrendSettings:           EmptyTrendSignalSettingFactory(),
		selectedTrendSignalsSettings:  [],
		isTrendSettingsLoading:        false,
		availableCommonSignals:        [],
		availableDistinctSignalGroups: [],
		CommonGroupExpanded:           true,
		DistinctGroupExpanded:         true,
		expandedDistinctAssetGroups:   [],
		ShowSignalNameDialog:          false,
		menuOpened:                    false,
		noDataAvailable:               false,
		filterText:                    "",

	};
};

/**
 * @summary Função que cria um objeto modelo que consta:
 * Id do sinal;
 * Nome do sinal de tendência;
 * Último valor coletado;
 * Última data/hora da coleta;
 * @returns {object} Um objeto modelo de tendência.
 */
const EmptyTrendSignalSettingFactory = function()
{
	return {
		id:                  -1,
		name:                "",
		unit:                "",
		monitoringElementId: 0,
		commonSignals:       [],
		distinctSignals:     []
	};
};

/**
 * @summary Função que cria um objeto que consta:
 * Id do ativo.
 * Objetos com os sinais de tendência de valores global do ativo;
 * @param {object} view - Objeto modelo inicializado.
 * @returns {object} Um objeto modelo da planta hidroelétrica.
*/
const TrendSignalSettingFactory = function(view = EmptyTrendSignalSettingFactory())
{
	return {
		trendSettingId:      view.id,
		monitoringElementId: view.monitoringElementId,
		name:                view.name,
		unit:                view.unit,
	};
};

/**
 * @summary Função que cria um objeto que consta:
 * Id do ativo.
 * Objetos com os sinais de tendência de valores global do ativo;
 * @param {object} view - Objeto modelo inicializado.
 * @returns {object} Um objeto modelo da planta hidroelétrica.
*/
const FrequencyTrendSignalSettingFactory = function(view = EmptyTrendSignalSettingFactory())
{
	return {
		assetName:               view.assetName,
		monitoringElementId:     view.monitoringElementId,
		frequencyTrendSettingId: view.frequencyTrendSettingId,
		name:                    view.name,
		unit:                    view.unit,
	};
};

/**
 * @summary Função que cria um objeto que consta:
 * Id do ativo.
 * Objetos com os sinais de tendência de valores global do ativo;
 * @param {object} view - Objeto modelo inicializado.
 * @returns {object} Um objeto modelo da planta hidroelétrica.
*/
const CreateAssetsTrendSignalSetting = function(view = EmptyTrendSignalSettingFactory())
{
	return {
		name:                view.assetName,
		monitoringElementId: view.assetMonitoringElementId,
		commonSignals:       (view.commonSignals || []).map(signal => TrendSignalSettingFactory(signal)),
		distinctSignals:     (view.distinctSignals || []).map(signal => TrendSignalSettingFactory(signal)),
	};
};

/**
 * @summary Função que cria um objeto que consta:
 * Id do ativo.
 * Objetos com os sinais de tendência do ativo;
 * @param {object} view - Objeto modelo inicializado.
 * @returns {object} Um objeto modelo da planta hidroelétrica.
*/
const CreateAssetsFrequencyTrendSignalSetting = function(view = {})
{
	return {
		name:                view.assetName,
		monitoringElementId: view.assetMonitoringElementId,
		commonSignals:       (view.commonFrequencySignals || []).map(signal => FrequencyTrendSignalSettingFactory(signal)),
		distinctSignals:     (view.distinctFrequencySignals || []).map(signal => FrequencyTrendSignalSettingFactory(signal))
	};
};

/**
 * @summary Obtém as configurações de sinais de tendência (Valores Global) para um conjunto de ativos.
 * @param {array} meIds Identificadores dos elementos de monitoração do tipo turbina ou unidade geradora.
 * @returns Conjunto de sinais de tendência de suas respectivas turbinas.
 */
const GetTrendSignalSettingsByAssetsMonitoringElements = async function(meIds = [])
{
	const resp = await Controller.TrendSettings.GetSignalsByAssetsMonitoringElements(meIds);

	if (resp.status === 204)
	{
		return [];
	}

	return resp.data.map((signals) =>
	{
		return CreateAssetsTrendSignalSetting(signals);
	});
};

/**
 * @summary Cria o objeto modelo de sinais de tendência de frequência .
 * @param {array} generatingUnitsMonitoringElementsIds
 * @param {boolean} onlyCalculated
 * @returns {object} Objeto modelo.
 */
const FactoryCreateFrequencyTrendSignalModel = function(ids, onlyCalculated)
{
	return {
		generatingUnitsMonitoringElementsIds: ids,
		onlyCalculated:                       onlyCalculated,
	};
};

/**
 * @summary Obtém as configurações de sinais de tendência de frequência para um conjunto de ativos.
 * @returns {object} Conjunto de sinais de tendência de suas respectivas turbinas.
 */
const GetFrequencyTrendSignalSettingsByAssetsMonitoringElements = async function(model)
{
	const resp = await Controller.FrequencyTrendSettings.GetFrequencyTrendSignalSettingsByAssetsMonitoringElements(model);

	if (resp.status === 204)
	{
		return [];
	}

	const trendSignals = resp.data.map((signals) =>
	{
		return CreateAssetsFrequencyTrendSignalSetting(signals);
	});
	store.dispatch(`Trend/TrendMenu/FrequencyTrendSignals/${SET}`, trendSignals);
	store.dispatch(`FrequencyTrendLimitConfiguration/LimitConfigurator/${SET}`, trendSignals);

	return trendSignals;
};

export default {
	GetTrendSignalSettingsByAssetsMonitoringElements,
	GetFrequencyTrendSignalSettingsByAssetsMonitoringElements,
	EmptyFrequencyTrendSignalStateFactory,
	EmptyTrendSignalSettingsStateFactory,
	TrendSignalSettingFactory,
	FactoryCreateFrequencyTrendSignalModel,
	FrequencyTrendSignalSettingFactory
};